import PropTypes from 'prop-types';
import { useState } from 'react';
import { BsX } from 'react-icons/bs';
import { MdOutlineListAlt, MdAdd, MdRemove } from 'react-icons/md';
import { recursiveFlatMap } from '@/pages/company-management/company-products/utils';
import Styled from './categories-filter-style';

const CategoryResolver = ({ category, activeFilters, handleSelectorClick }) => {
  const [open, setOpen] = useState(false);
  return (
    <Styled.Section>
      <Styled.CategoryHeader onClick={() => setOpen(!open)} isOpen={open}>
        <Styled.HeaderItem>
          {category.icon.includes('http') ? (
            <Styled.Img src={category.icon} />
          ) : (
            <MdOutlineListAlt />
          )}
          <Styled.HeaderTitle>{category.title}</Styled.HeaderTitle>
        </Styled.HeaderItem>
        <Styled.CategoryActionIcon>
          {open ? <MdRemove /> : <MdAdd />}
        </Styled.CategoryActionIcon>
      </Styled.CategoryHeader>
      {open && (
        <Styled.Content>
          {category?.children?.map((item, idx) => (
            <Styled.OEMButton
              key={`oem_item_${idx + 1}`}
              isSelected={activeFilters.includes(item.id)}
              onClick={() => handleSelectorClick(item?.id)}
            >
              {item.name}
            </Styled.OEMButton>
          ))}
        </Styled.Content>
      )}
    </Styled.Section>
  );
};

CategoryResolver.propTypes = {
  category: PropTypes.shape({
    title: PropTypes.string,
    icon: PropTypes.string,
    children: PropTypes.arrayOf(PropTypes.object),
  }),
  handleSelectorClick: PropTypes.func,
  activeFilters: PropTypes.arrayOf(PropTypes.string),
};

CategoryResolver.defaultProps = {
  category: null,
  handleSelectorClick: () => {},
  activeFilters: [],
};

/**
 * CategoriesSelector Component
 * @param {object} props - Component props
 * @param {object} props.data - categories data
 * @param {Function} props.handleSelectorClick - Function to handle  add/remove filter click
 * @param {Array} props.activeFilters - Array of active filters
 * @param {Function} props.handleClearAll - Function to clear all filters
 * @param {Function} props.handleRemoveSelectedItemFromFilter - Function to remove a single filter
 * @returns {JSX.Element} CategoriesSelector component
 */
const CategoriesSelector = ({
  data,
  handleSelectorClick,
  activeFilters,
  handleClearAll,
  handleRemoveSelectedItemFromFilter,
}) => {
  const allCategories = data;
  const getActiveFilterName = (id) => {
    const categories = recursiveFlatMap(allCategories, []);
    return categories.find((c) => c.id === id)?.name;
  };

  return (
    <Styled.CategoriesSelector>
      <Styled.Section>
        <Styled.Header>
          <Styled.HeaderItem>Active Filters</Styled.HeaderItem>
          <Styled.HeaderItemAction onClick={handleClearAll}>
            Clear All <BsX />
          </Styled.HeaderItemAction>
        </Styled.Header>
        <Styled.Content>
          {activeFilters.map((id, idx) => (
            <Styled.SelectedFilterItem key={`selectedfilteritem_${idx + 1}`}>
              <Styled.FilterText>{getActiveFilterName(id)}</Styled.FilterText>
              <BsX onClick={() => handleRemoveSelectedItemFromFilter(id)} />
            </Styled.SelectedFilterItem>
          ))}
        </Styled.Content>
      </Styled.Section>
      {allCategories?.map((category) => (
        <CategoryResolver
          key={category.id}
          category={category}
          handleSelectorClick={handleSelectorClick}
          activeFilters={activeFilters}
        />
      ))}
    </Styled.CategoriesSelector>
  );
};

CategoriesSelector.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object),
  handleSelectorClick: PropTypes.func,
  activeFilters: PropTypes.arrayOf(PropTypes.string),
  handleClearAll: PropTypes.func,
  handleRemoveSelectedItemFromFilter: PropTypes.func,
};

CategoriesSelector.defaultProps = {
  data: [],
  handleSelectorClick: () => {},
  activeFilters: [],
  handleClearAll: () => {},
  handleRemoveSelectedItemFromFilter: () => {},
};

export default CategoriesSelector;
